import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, rootActions, useAppSelector } from '../data/store';
import { debounce } from 'lodash';
import { getSearchData } from '../../search-desktop/data/data-service';
import SearchIcon from '../../../../ui/svg/common/search';
import { getGeneralSearchUrl } from '../../search-desktop/domain/search-provider';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';

const SearchBox = () => {
    const dispatch = useAppDispatch();

    const query = useAppSelector(store => store.root.query);
    const isOpened = useAppSelector(store => store.root.isOpened);

    const textBoxRef = useRef<HTMLInputElement>(null);

    useEffect(() => {

        // on page load -> focus on the field
        if(!textBoxRef || !textBoxRef.current || !isOpened) return;
        textBoxRef.current.focus();
    }, [isOpened]);

    // ------------------ SEARCH -----------------------

    const search = async (inputText: string) => {

        if(inputText.trim() === ''){
            dispatch(
                rootActions.main({
                    filtered: [],
                    query: '',
                    selectedItemIndex: -1,
                    touched: false,
                })
            );
            return;
        }

        const response = await getSearchData(inputText);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){
            return;
        }

        const responseResults = response.data.relatedData || [];

        if(inputText.length > 1) {
            // q: {query} | rc: {number of results}
            // q: api | rc: 4
            sendGAEvent('AutoCompleteQuery', `q: ${ inputText.toLowerCase() } | rc: ${ responseResults.length }`);
        }

        dispatch(
            rootActions.main({
                filtered: responseResults,
                // query: inputText,
                selectedItemIndex: -1,
                touched: true,
            })
        );

        open();
    };

    // debounce is an external dependency, and it can't be replaced with inline function
    // eslint-disable-next-line
    const debounced = useCallback(
        debounce(
            (inputText) => search(inputText),450
        ),
        []
    );

    const handleInput = (evt: ChangeEvent<HTMLInputElement>) => {
        dispatch(
            rootActions.main({
                query: evt.target.value,
            })
        );

        debounced(evt.target.value);
    };

    // ---------------------------------------------------------

    const open = () => {
        dispatch(
            rootActions.main({
                isOpened: true,
            })
        );
    };

    const close = useCallback(() => {
        dispatch(
            rootActions.main({
                isOpened: false,
            })
        )
    }, [dispatch]);

    const handleKeyUp = (evt: KeyboardEvent) => {

        if(evt.key === 'Enter') {
            evt.preventDefault();
            evt.stopPropagation();
            close();
            window.location.href = getGeneralSearchUrl(query);
        }

        if(evt.key === 'Escape') {
            close();
        }
    };

    return (
        <label htmlFor="header-site-search-m" className="flex items-center border rounded-full overflow-hidden mx-6 mt-6 px-6 py-3">
            <input
                onInput={ handleInput }
                onKeyUp={ handleKeyUp }
                value={ query }
                className="bg-white flex-1 text-tiny text-gray-700 outline-none mr-2"
                type="text"
                name="search"
                id="header-site-search-m"
                placeholder="Search"
                autoComplete="off"
                ref={ textBoxRef }
            />
            <SearchIcon width="15px" height="15px" color="#4A4A4A" title="Search" />
        </label>
    )
};

export default SearchBox;